import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  TablePagination,
  TableSortLabel,
  Box,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Wc as GenderIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function ClientesAdmin() {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('nombre_cliente');
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    fetchClientes();
  }, []);

  useEffect(() => {
    const filterAndSortClientes = () => {
      let filtered = [...clientes];
      
      if (searchTerm) {
        filtered = filtered.filter(cliente =>
          Object.values(cliente)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredClientes(filtered);
      setPage(0);
    };

    filterAndSortClientes();
  }, [clientes, searchTerm, order, orderBy]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/clientes`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setClientes(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los clientes:', error);
      setError('Error al cargar los clientes. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar el cliente ${nombre}?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/clientes/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchClientes();
      } catch (error) {
        console.error('Error al eliminar el cliente:', error);
        setError('Error al eliminar el cliente.');
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const getInitials = (nombre, apellido) => {
    return `${nombre?.charAt(0) || ''}${apellido?.charAt(0) || ''}`.toUpperCase();
  };

  // Vista móvil con tarjetas
  const MobileClientCard = ({ cliente }) => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 3,
        border: '1px solid rgba(26, 35, 126, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
              color: 'white',
              fontWeight: 'bold',
              mr: 2
            }}
          >
            {getInitials(cliente.nombre_cliente, cliente.apellido_cliente)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
              {cliente.nombre_cliente} {cliente.apellido_cliente}
            </Typography>
            <Chip 
              label={cliente.genero || 'No especificado'} 
              size="small"
              sx={{ 
                backgroundColor: cliente.genero === 'M' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(233, 30, 99, 0.1)',
                color: cliente.genero === 'M' ? '#2196F3' : '#E91E63'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {cliente.tipo_documento}: {cliente.numero_documento}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {cliente.email_cliente}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {cliente.telefono_cliente}
            </Typography>
          </Box>

          {cliente.direccion_cliente && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HomeIcon sx={{ color: '#4FC3F7', fontSize: '1.1rem' }} />
              <Typography variant="body2" color="text.secondary">
                {cliente.direccion_cliente}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/home/clientes/editar/${cliente.id_cliente}`)}
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.main' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(cliente.id_cliente, `${cliente.nombre_cliente} ${cliente.apellido_cliente}`)}
            sx={{ 
              bgcolor: 'error.light', 
              color: 'white',
              '&:hover': { bgcolor: 'error.main' }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
       
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            Gestión de Clientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra la información de tus clientes
          </Typography>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/clientes/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Cliente
          </Button>
        )}
        
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por nombre, email, teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredClientes
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((cliente) => (
              <MobileClientCard key={cliente.id_cliente} cliente={cliente} />
            ))}
          
          {filteredClientes.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredClientes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ 
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)' 
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'nombre_cliente'}
                      direction={orderBy === 'nombre_cliente' ? order : 'asc'}
                      onClick={createSortHandler('nombre_cliente')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Cliente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Documento
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'email_cliente'}
                      direction={orderBy === 'email_cliente' ? order : 'asc'}
                      onClick={createSortHandler('email_cliente')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'telefono_cliente'}
                      direction={orderBy === 'telefono_cliente' ? order : 'asc'}
                      onClick={createSortHandler('telefono_cliente')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Teléfono
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClientes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((cliente, index) => (
                    <TableRow 
                      key={cliente.id_cliente}
                      sx={{ 
                        '&:nth-of-type(even)': { 
                          backgroundColor: 'rgba(26, 35, 126, 0.02)' 
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(79, 195, 247, 0.1)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s ease'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                              color: 'white',
                              fontSize: '0.9rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {getInitials(cliente.nombre_cliente, cliente.apellido_cliente)}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {cliente.nombre_cliente} {cliente.apellido_cliente}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cliente.genero === 'M' ? 'Masculino' : cliente.genero === 'F' ? 'Femenino' : 'No especificado'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cliente.numero_documento}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cliente.tipo_documento}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {cliente.email_cliente}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cliente.telefono_cliente}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar cliente">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/home/clientes/editar/${cliente.id_cliente}`)}
                              sx={{ 
                                bgcolor: 'primary.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.main' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cliente">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(cliente.id_cliente, `${cliente.nombre_cliente} ${cliente.apellido_cliente}`)}
                              sx={{ 
                                bgcolor: 'error.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'error.main' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredClientes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/clientes/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}

export default ClientesAdmin;